import { PM_PROVIDERS } from 'common/constants/constants';
import { API } from '../constants/api-urls';
import axios from '../requester';
import _ from 'lodash';

export const getPulseData = async ({
  iteration,
  team,
  sensor_id,
  timelineStartDate,
  projectId,
  teamPMConfigurationEnabled = false,
  pulseConfigSetting,
  fetchType // New parameter
}) => {
  const pullRequest = `pull_request {
    id
    title
    stats
    state
    html_url
    created_at
    first_commented_at
    issuer_id
  }`;
  const release = `release {
    id
    name
    body
    published_at
    publisher_id
  }`;
  const issueBranches = `branches {
    contributor_ids
    id
    name
    state
    stats
    repository_name
    first_committed_at
    alerts
    contributor_ids
    ${pullRequest}
    ${release}
    timeline(after: "${timelineStartDate}")
  }`;
  const issueProps = `
    properties {
      story_points
    }
    id
    title
    html_url
    issue_type
    issue_state
    provider_key
    provider_type
    provider_state
    provider_type_icon_url
    parent_provider_id
    attributes
    provider_attributes
    shadow_work
    ${issueBranches}
  `;
  const unmatchedBranchesProps = `
    contributor_ids
    id
    name
    state
    stats
    repository_name
    first_committed_at
    alerts
    http_url
    last_activity
    ${pullRequest}
    ${release}
    timeline(after: "${timelineStartDate}")
  `;
  const allBranchesProps = `
    id
    name
    contributor_ids
    state
  `;
  const withProjectId =
    sensor_id === PM_PROVIDERS.CLUBHOUSE || teamPMConfigurationEnabled;
  const teamId = team.id;
  const iterationStartDate = iteration.start_date;
  const issuesQuery = `issue_type: ["task"], ${
    iteration.type !== 'weekly'
      ? `iteration_id: ${
          iteration.id
        }, branch_updated: ["${iterationStartDate}"], team_id: ${teamId}${
          withProjectId ? `, project_id: ${team.project_id}` : ''
        }`
      : `limit: ${pulseConfigSetting.kanbanLimit}, offset: 0, updated: ["${iterationStartDate}"], branch_updated: ["${iterationStartDate}"], team_id: ${teamId}, project_id: ${projectId}`
  }`;
  const doneIssuesQuery = `issue_type: ["task"], ${
    iteration.type !== 'weekly'
      ? `iteration_id: ${
          iteration.id
        }, branch_updated: ["${iterationStartDate}"], team_id: ${teamId}${
          withProjectId ? `, project_id: ${team.project_id}` : ''
        }`
      : `limit: ${pulseConfigSetting.kanbanLimit}, offset: 0, completed: ["${iterationStartDate}"], branch_updated: ["${iterationStartDate}"], team_id: ${teamId}, project_id: ${projectId}`
  }`;
  const unmatchedBranchFilters = `team_id:${team.id}, before: "${iteration.end_date}", after: "${iteration.start_date}", limit: 1000, full_details: true, enrich_alerts: true`;
  const allBranchFilters = `team_id:${team.id}, before: "${iteration.end_date}", after: "${iteration.start_date}", limit: 1000`;

  let query = '';

  switch (fetchType) {
    case 'inProgIssues':
      query = `{
        inProgIssues: issues(issue_state: ["in-progress"], ${issuesQuery}) {
          ${issueProps}
          ${
            sensor_id === PM_PROVIDERS.JIRA
              ? `subtasks {
            ${issueProps}
          }`
              : ''
          }
        }
      }`;
      break;
    case 'createdIssues':
      query = `{
        createdIssues: issues(issue_state: ["created"], ${issuesQuery}) {
          ${issueProps}
          ${
            sensor_id === PM_PROVIDERS.JIRA
              ? `subtasks {
            ${issueProps}
          }`
              : ''
          }
        }
      }`;
      break;
    case 'doneIssues':
      query = `{
        doneIssues: issues(issue_state: ["done"], ${doneIssuesQuery}) {
          ${issueProps}
          ${
            sensor_id === PM_PROVIDERS.JIRA
              ? `subtasks {
            ${issueProps}
          }`
              : ''
          }
        }
      }`;
      break;
    case 'branches':
      query = `{
        unmatched: branches(no_issue_assigned: true, ${unmatchedBranchFilters}){
          ${unmatchedBranchesProps}
        },
        all: branches(${allBranchFilters}){
          ${allBranchesProps}
        }
      }`;
      break;
    default:
      throw new Error('Invalid fetchType');
  }

  const response = await axios.post(API.GRAPHQL_API_PATH, { query });
  return response.data;
};

export const getIssuesByIterationByTeam = async ({
  iteration,
  team,
  sensor_id,
  projectId,
  isTeamPMConfigurationEnabled = false
}) => {
  const pullRequest = `pull_request {
      id
      title
      state
      html_url
      created_at
      first_commented_at
      issuer_id
    }`;
  const issueBranches = `branches {
      contributor_ids
      id
      name
      state
      repository_name
      first_committed_at
      alerts
      contributor_ids
      ${pullRequest}
    }`;
  const issueProps = `
      id
      title
      html_url
      issue_type
      issue_state
      provider_key
      provider_type
      provider_state
      provider_type_icon_url
      parent_provider_id
      ${issueBranches}
  `;
  const withProjectId =
    sensor_id === PM_PROVIDERS.CLUBHOUSE || isTeamPMConfigurationEnabled;
  const teamId = team.id;
  const iterationStartDate = iteration.start_date;
  const issuesQuery = `issue_type: ["task"], ${
    iteration.type !== 'weekly'
      ? `iteration_id: ${
          iteration.id
        }, branch_updated: ["${iterationStartDate}"], team_id: ${teamId}${
          withProjectId ? `, project_id: ${team.project_id}` : ''
        }`
      : `updated: ["${iterationStartDate}"], branch_updated: ["${iterationStartDate}"], team_id: ${teamId}, project_id: ${projectId}`
  }`;
  const query = `{
    issues(${issuesQuery}) {
      ${issueProps}
      ${
        sensor_id === PM_PROVIDERS.JIRA
          ? `subtasks {
        ${issueProps}
      }`
          : ''
      }
    }
  }`;
  const response = await axios.post(API.GRAPHQL_API_PATH, { query });
  return response.data;
};

export const extractAlertsFromBranch = (branch, contributors) => {
  const pr = branch.pull_request;

  return (_.get(branch, 'alerts', []) || []).map(alert => {
    const issuer_id = _.get(pr, 'issuer_id', null);
    const issuer_name = _.get(
      contributors.find(c => c.id === issuer_id),
      'name',
      null
    );
    return {
      id: _.get(alert, 'id', null),
      type: _.get(alert, 'alert_type', null),
      pr_first_commented_at: _.get(pr, 'first_commented_at', null),
      pr_created_at: _.get(pr, 'created_at', null),
      pr_html: _.get(pr, 'html_url', null),
      pr_issuer_name: issuer_name
    };
  });
};
